import { useEffect, useRef, useState } from 'react';

import { Center, Loader, Text } from '@mantine/core';

import * as Sentry from '@sentry/react';

import {
  ANCHOR_HIGHLIGHT_EVENT,
  ANCHOR_PERSIST_EVENT,
  consumePendingAnchorHighlight,
  getPersistentAnchorHighlight,
} from '@/lib/anchorHighlight';
import type { DocxModel } from '@/lib/ooxml';
import { vnextApiClient } from '@/services/vnext-api';
import { DocxBlockList } from './DocxBlockList';
import classes from './DocxContent.module.css';

const COVERAGE_FLOOR_PCT = 98;

const IMAGE_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  tif: 'image/tiff',
  tiff: 'image/tiff',
};

type DocxContentProps =
  | { reportId: string; docxUrl?: never }
  | { docxUrl: string; reportId?: never };

async function buildImageUrls(model: DocxModel): Promise<Record<string, string>> {
  const used = new Set<string>();
  for (const block of model.blocks) {
    for (const seg of block.segments) {
      if (seg.type === 'image') {
        used.add(seg.rId);
      }
    }
  }
  const urls: Record<string, string> = {};
  for (const rId of used) {
    const path = model.mediaTargets[rId];
    if (!path) {
      continue;
    }
    const bytes = await model.readPart(path);
    if (!bytes) {
      continue;
    }
    const ext = path.split('.').pop()?.toLowerCase() ?? '';
    const type = IMAGE_MIME[ext];
    if (!type) {
      continue; // EMF/WMF and friends: the Presentation tab renders those.
    }
    urls[rId] = URL.createObjectURL(new Blob([bytes as BlobPart], { type }));
  }
  return urls;
}

/**
 * Content-layer report view: the docx parsed on demand into normalized,
 * selectable typography. Every block carries `data-para-id` — the anchor
 * substrate for feedback selections and scope-component mapping. Page
 * fidelity questions belong to the Presentation (PDF) tab, permanently.
 */
export function DocxContent({ reportId, docxUrl }: Readonly<DocxContentProps>) {
  const [model, setModel] = useState<DocxModel | null>(null);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const urlsRef = useRef<Record<string, string>>({});
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Feedback-anchor highlighting: consume asks from the AI QA panel. The
  // post-load consume covers asks fired before this view existed (the tab
  // flip mounts it, keepMounted=false); the listener covers live asks.
  useEffect(() => {
    if (!model) {
      return;
    }
    const applyHighlight = () => {
      const ids = consumePendingAnchorHighlight();
      const root = wrapperRef.current;
      if (!ids || !root) {
        return;
      }
      const blocks = ids
        .map((id) => root.querySelector(`[data-para-id="${CSS.escape(id)}"]`))
        .filter((el): el is HTMLElement => el instanceof HTMLElement);
      if (blocks.length === 0) {
        return;
      }
      blocks[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      blocks.forEach((el) => el.classList.add(classes.anchorFlash));
      window.setTimeout(() => {
        blocks.forEach((el) => el.classList.remove(classes.anchorFlash));
      }, 2600);
    };
    applyHighlight();
    window.addEventListener(ANCHOR_HIGHLIGHT_EVENT, applyHighlight);
    return () => window.removeEventListener(ANCHOR_HIGHLIGHT_EVENT, applyHighlight);
  }, [model]);

  // Compose-time persistent marker: while feedback context is captured,
  // its blocks stay tinted so it's clear what the message is about.
  useEffect(() => {
    if (!model) {
      return;
    }
    const sync = () => {
      const root = wrapperRef.current;
      if (!root) {
        return;
      }
      const ids = new Set(getPersistentAnchorHighlight());
      root.querySelectorAll<HTMLElement>('[data-para-id]').forEach((el) => {
        el.classList.toggle(classes.anchorPersist, ids.has(el.dataset.paraId ?? ''));
      });
    };
    sync();
    window.addEventListener(ANCHOR_PERSIST_EVENT, sync);
    return () => window.removeEventListener(ANCHOR_PERSIST_EVENT, sync);
  }, [model]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const url = docxUrl ?? (await vnextApiClient.downloadReportDocx(reportId!)).url;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch document (${response.status})`);
        }
        const bytes = await response.arrayBuffer();
        if (cancelled) {
          return;
        }
        const { parseDocx } = await import('@/lib/ooxml');
        const parsed = await parseDocx(bytes);
        const urls = await buildImageUrls(parsed);
        if (cancelled) {
          Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
          return;
        }
        urlsRef.current = urls;
        setModel(parsed);
        setImageUrls(urls);

        if (parsed.coverage.pct < COVERAGE_FLOOR_PCT) {
          Sentry.captureMessage('docx content coverage below floor', {
            level: 'warning',
            extra: {
              reportId,
              coveragePct: parsed.coverage.pct,
              unknownBlockTags: parsed.coverage.unknownBlockTags,
              unknownInlineTags: parsed.coverage.unknownInlineTags,
            },
          });
        }
      } catch (err) {
        if (!cancelled) {
          Sentry.captureException(err);
          setError(err instanceof Error ? err.message : 'Failed to load document');
        }
      }
    }

    load();
    return () => {
      cancelled = true;
      Object.values(urlsRef.current).forEach((u) => URL.revokeObjectURL(u));
      urlsRef.current = {};
    };
  }, [reportId, docxUrl]);

  if (error) {
    return (
      <Center py="xl">
        <Text c="red" size="sm">
          {error}
        </Text>
      </Center>
    );
  }
  if (!model) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  return (
    <div className={classes.wrapper} ref={wrapperRef}>
      {model.coverage.pct < COVERAGE_FLOOR_PCT && (
        <Text size="xs" c="dimmed" className={classes.coverageNote}>
          Content view covers {model.coverage.pct.toFixed(1)}% of this document's text — use the
          Presentation tab for the complete rendering.
        </Text>
      )}
      <DocxBlockList model={model} imageUrls={imageUrls} />
    </div>
  );
}
